/**
 * SupplierSettings.jsx — Settings page for the supplier portal.
 *
 * Three tabs:
 *   1. Profile — edit phone
 *   2. Notifications — per-category/channel preference toggles
 *   3. Global — digest mode, quiet hours, timezone
 */
import { useEffect, useState, useCallback } from "react";
import { User, Bell, Settings2, Save, Loader2, Key, Copy, Trash2, Plus } from "lucide-react";
import Swal from "../../utils/apparifySwal";
import { PageHeader, BtnPrimary, BtnSecondary } from "../../components/erp";
import { supplierSettingsAPI, supplierInviteCodeAPI } from "../services/supplierApi";

const TABS = [
  { key: "profile", label: "Profile", Icon: User },
  { key: "notifications", label: "Notifications", Icon: Bell },
  { key: "invite-codes", label: "Invite Codes", Icon: Key },
  { key: "global", label: "Global Settings", Icon: Settings2 },
];

const CATEGORY_LABELS = {
  PROCUREMENT:  "Procurement",
  SUPPLIER_OPS: "Supplier Operations",
  SYSTEM:       "System",
};

const CHANNEL_LABELS = { INAPP: "In-App", EMAIL: "Email", SMS: "SMS", PUSH: "Push" };

export default function SupplierSettings() {
  const [tab, setTab] = useState("profile");

  return (
    <div className="operations-page">
      <PageHeader title="Settings" subtitle="Manage your profile and notification preferences" />

      {/* Tab bar */}
      <div className="flex gap-1 overflow-x-auto border-b border-neutral-200 dark:border-neutral-700">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={[
              "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px",
              tab === t.key
                ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                : "border-transparent text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200",
            ].join(" ")}
          >
            <t.Icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "invite-codes" && <InviteCodesTab />}
      {tab === "global" && <GlobalTab />}
    </div>
  );
}

/* ═══════════════════════════════════════════
   PROFILE TAB
   ═══════════════════════════════════════════ */
function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ phone: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    supplierSettingsAPI.getProfile()
      .then((res) => {
        const d = res.data;
        setProfile(d);
        setForm({ phone: d.phone || "" });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await supplierSettingsAPI.updateProfile(form);
      setProfile(res.data);
      Swal.fire({ icon: "success", title: "Profile updated", timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed to update", text: err?.response?.data?.message || "Something went wrong" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  const dirty = profile && (
    form.phone     !== (profile.phone || "")
  );

  return (
    <div className="operations-surface max-w-xl space-y-5 p-5 sm:p-6">
      <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Your Profile</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Phone</label>
          <input type="tel" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Email</label>
          <input type="email" value={profile?.email || ""} disabled className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400 cursor-not-allowed" />
        </div>
      </div>

      <div className="flex justify-end">
        <BtnPrimary onClick={handleSave} disabled={saving || !dirty}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Changes
        </BtnPrimary>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   NOTIFICATIONS TAB
   ═══════════════════════════════════════════ */
function NotificationsTab() {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    supplierSettingsAPI.getNotificationSettings()
      .then((res) => setPrefs(res.preferences || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (category, channel) => {
    setPrefs((prev) =>
      prev.map((p) => (p.category === category && p.channel === channel && !p.required ? { ...p, enabled: !p.enabled } : p))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await supplierSettingsAPI.updateNotificationPreferences(prefs);
      setPrefs(res.preferences || prefs);
      Swal.fire({ icon: "success", title: "Preferences saved", timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to save", text: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  const categories = Object.keys(CATEGORY_LABELS);
  const grouped = {};
  for (const cat of categories) grouped[cat] = prefs.filter((p) => p.category === cat);

  return (
    <div className="max-w-2xl space-y-5">
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Control how you receive supplier notifications. In-App notifications marked as required cannot be disabled.
      </p>

      {categories.map((cat) => (
        <div key={cat} className="operations-surface p-5">
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 mb-3">{CATEGORY_LABELS[cat]}</h4>
          <div className="grid grid-cols-5 gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-2 px-1">
            <span />
            {Object.values(CHANNEL_LABELS).map((ch) => (
              <span key={ch} className="text-center font-medium">{ch}</span>
            ))}
          </div>
          {grouped[cat]?.length ? (
            <div className="grid grid-cols-5 gap-2 items-center px-1">
              <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{CATEGORY_LABELS[cat]}</span>
              {Object.keys(CHANNEL_LABELS).map((ch) => {
                const pref = grouped[cat].find((p) => p.channel === ch);
                if (!pref) return <span key={ch} />;
                return (
                  <div key={ch} className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => toggle(cat, ch)}
                      disabled={pref.required}
                      className={[
                        "h-5 w-9 rounded-full transition-colors relative",
                        pref.enabled ? "bg-emerald-500 dark:bg-emerald-600" : "bg-neutral-300 dark:bg-neutral-600",
                        pref.required ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                      ].join(" ")}
                    >
                      <span className={["absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform", pref.enabled ? "translate-x-4" : "translate-x-0.5"].join(" ")} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-neutral-400">No preferences available.</p>
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <BtnPrimary onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Preferences
        </BtnPrimary>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   INVITE CODES TAB
   ═══════════════════════════════════════════ */
function InviteCodesTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "", maxUses: 10 });
  const [newCode, setNewCode] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    supplierInviteCodeAPI.list()
      .then((res) => setCodes(res.data || res.codes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await supplierInviteCodeAPI.generate({
        label: form.label.trim() || null,
        maxUses: Number(form.maxUses) || 10,
      });
      const data = res.data || res;
      setNewCode(data.code);
      setShowForm(false);
      setForm({ label: "", maxUses: 10 });
      load();
      Swal.fire({
        icon: "success",
        title: "Invite code generated",
        html: `<p class="text-sm text-neutral-600">Share this code with businesses. <strong>Copy it now</strong> — it cannot be recovered later.</p>
               <code class="block mt-2 p-2 bg-neutral-100 rounded text-lg font-mono select-all">${data.code}</code>`,
        confirmButtonText: "Got it",
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to generate", text: e?.message || "Something went wrong" });
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (codeId) => {
    const { isConfirmed } = await Swal.fire({
      title: "Revoke invite code?",
      text: "This code will no longer be usable by businesses.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revoke",
      confirmButtonColor: "#e11d48",
    });
    if (!isConfirmed) return;
    try {
      await supplierInviteCodeAPI.revoke(codeId);
      load();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to revoke", text: e?.message || "Something went wrong" });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({ icon: "success", title: "Copied!", timer: 1000, showConfirmButton: false });
    }).catch(() => {});
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Generate invite codes so businesses can connect with you instantly.
        </p>
        <BtnPrimary onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" /> Generate Code
        </BtnPrimary>
      </div>

      {/* New code just generated */}
      {newCode && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider mb-1">New Invite Code</p>
              <code className="text-lg font-mono text-emerald-900 dark:text-emerald-100 select-all">{newCode}</code>
            </div>
            <button onClick={() => copyToClipboard(newCode)}
              className="rounded-lg border border-emerald-300 bg-white p-2 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 dark:hover:bg-emerald-800 transition">
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">Copy this code now — it cannot be recovered later.</p>
          <button onClick={() => setNewCode(null)} className="mt-2 text-xs text-emerald-700 dark:text-emerald-400 underline">Dismiss</button>
        </div>
      )}

      {/* Generate form */}
      {showForm && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 space-y-3">
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">New Invite Code</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Label (optional)</label>
              <input type="text" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. For Manila clients"
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Max Uses</label>
              <input type="number" min="1" max="1000" value={form.maxUses} onChange={(e) => setForm((f) => ({ ...f, maxUses: e.target.value }))}
                className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <BtnSecondary onClick={() => setShowForm(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleGenerate} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
              Generate
            </BtnPrimary>
          </div>
        </div>
      )}

      {/* Codes list */}
      {codes.length === 0 ? (
        <div className="operations-surface p-8 text-center">
          <Key className="h-8 w-8 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">No invite codes yet</p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Generate a code to let businesses connect with you.</p>
        </div>
      ) : (
        <div className="operations-surface divide-y divide-neutral-100 dark:divide-neutral-800">
          {codes.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-5 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {c.label || `Code #${c.id}`}
                  </span>
                  <span className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                    c.status === "ACTIVE"
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : c.status === "USED"
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400",
                  ].join(" ")}>
                    {c.status}
                  </span>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Uses: {c.current_uses}/{c.max_uses}
                  {c.expires_at && <> · Expires: {new Date(c.expires_at).toLocaleDateString()}</>}
                  {c.created_at && <> · Created: {new Date(c.created_at).toLocaleDateString()}</>}
                </p>
              </div>
              {c.status === "ACTIVE" && (
                <button onClick={() => handleRevoke(c.id)} title="Revoke"
                  className="rounded-lg p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   GLOBAL SETTINGS TAB
   ═══════════════════════════════════════════ */
function GlobalTab() {
  const [settings, setSettings] = useState({ digest_mode: "OFF", timezone: "UTC", quiet_hours_start: null, quiet_hours_end: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    supplierSettingsAPI.getNotificationSettings()
      .then((res) => { if (res.global) setSettings(res.global); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await supplierSettingsAPI.updateNotificationGlobal({
        digestMode: settings.digest_mode,
        timezone: settings.timezone,
        quietHoursStart: settings.quiet_hours_start,
        quietHoursEnd: settings.quiet_hours_end,
      });
      if (res.global) setSettings(res.global);
      Swal.fire({ icon: "success", title: "Settings saved", timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to save", text: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;

  return (
    <div className="operations-surface max-w-xl space-y-5 p-5 sm:p-6">
      <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Global Notification Settings</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Digest Mode</label>
          <select
            value={settings.digest_mode}
            onChange={(e) => setSettings((p) => ({ ...p, digest_mode: e.target.value }))}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
          >
            <option value="OFF">Off</option>
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Timezone</label>
          <input
            type="text"
            value={settings.timezone}
            onChange={(e) => setSettings((p) => ({ ...p, timezone: e.target.value }))}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
            placeholder="UTC"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Quiet Hours Start</label>
            <input
              type="time"
              value={settings.quiet_hours_start || ""}
              onChange={(e) => setSettings((p) => ({ ...p, quiet_hours_start: e.target.value || null }))}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Quiet Hours End</label>
            <input
              type="time"
              value={settings.quiet_hours_end || ""}
              onChange={(e) => setSettings((p) => ({ ...p, quiet_hours_end: e.target.value || null }))}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <BtnPrimary onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Settings
        </BtnPrimary>
      </div>
    </div>
  );
}
